import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateUser } from "@/api/users";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TableCell, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  format,
  formatDistanceToNow,
  isAfter,
  isBefore,
  isPast,
} from "date-fns";
import {
  MoreHorizontal,
  Edit,
  UserCog,
  Power,
  ChevronDown,
  Shield,
  Loader2,
  AlertCircle,
  ShieldAlert,
  CreditCard,
  Clock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { User, UserRole } from "@/types";
import { UserSubscription } from "@/types/subscription";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";

type UserTableProps = {
  users: User[];
  userRoles: UserRole[];
  userSubscriptions?: Map<number, UserSubscription>; // latest (any status)
  activeUserSubscriptions?: Map<number, UserSubscription>; // active only fallback
};

// Get color for role badge
const getRoleBadgeColor = (roleId: number): string => {
  // Special colors for specific roles
  if (roleId === 1) {
    // SuperAdmin - Red
    return "bg-red-500/10 text-red-500 border-red-500/20";
  }
  if (roleId === 5) {
    // Standard User - Blue
    return "bg-blue-500/10 text-blue-500 border-blue-500/20";
  }

  // Define a set of vibrant transparent background and text color pairs
  const colorPairs = [
    "bg-primary/10 text-primary border-primary/20",
    "bg-blue-500/10 text-blue-500 border-blue-500/20",
    "bg-amber-500/10 text-amber-500 border-amber-500/20",
    "bg-green-500/10 text-green-500 border-green-500/20",
    "bg-purple-500/10 text-purple-500 border-purple-500/20",
    "bg-rose-500/10 text-rose-500 border-rose-500/20",
    "bg-teal-500/10 text-teal-500 border-teal-500/20",
    "bg-orange-500/10 text-orange-500 border-orange-500/20",
    "bg-indigo-500/10 text-indigo-500 border-indigo-500/20",
  ];

  return colorPairs[roleId % colorPairs.length];
};

// Function to get role priority - lower number means higher priority
const getRolePriority = (roleId: number): number => {
  // Define role priorities (1 = highest, 10 = lowest)
  // You can adjust these priorities based on your system's hierarchy
  const rolePriorities: Record<number, number> = {
    1: 1, // SuperAdmin (highest priority)
    2: 2, // Admin
    3: 3, // Manager
    4: 4, // Editor
    5: 5, // Standard User
    // Add more role priorities as needed
  };

  return rolePriorities[roleId] || 10; // Default to lowest priority if not found
};

const UserTable = ({
  users,
  userRoles,
  userSubscriptions,
  activeUserSubscriptions,
}: UserTableProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const [isRolesDialogOpen, setIsRolesDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [unauthorizedMessage, setUnauthorizedMessage] = useState<string>("");

  // Check if current user has admin privileges to manage roles
  const hasAdminPrivileges = (): boolean => {
    if (!currentUser || !currentUser.roles) return false;

    // Check if the user has any system admin role
    return currentUser.roles.some((role) => role.isSystem === true);
  };

  // Get the current user's highest priority role
  const getCurrentUserHighestRolePriority = (): number => {
    if (
      !currentUser ||
      !currentUser.roleIds ||
      currentUser.roleIds.length === 0
    ) {
      return 100; // Very low priority if no roles
    }

    // Get the lowest priority number (highest priority role)
    return Math.min(...currentUser.roleIds.map(getRolePriority));
  };

  // Check if current user can manage a specific role
  const canManageRole = (roleId: number): boolean => {
    // Admin users can only manage roles with lower priority (higher priority number) than their highest role
    const currentUserHighestPriority = getCurrentUserHighestRolePriority();
    const targetRolePriority = getRolePriority(roleId);

    return currentUserHighestPriority < targetRolePriority;
  };

  // Update roles mutation
  const updateRolesMutation = useMutation({
    mutationFn: async () => {
      if (!selectedUser) throw new Error("No user selected");

      // Double-check permissions before submitting
      if (!hasAdminPrivileges()) {
        throw new Error("You don't have permission to manage user roles");
      }

      // Ensure we're not assigning any roles that the current user can't manage
      const unauthorizedRoles = selectedRoleIds.filter(
        (roleId) => !canManageRole(roleId)
      );
      if (unauthorizedRoles.length > 0) {
        throw new Error(
          "You don't have permission to assign some of these roles"
        );
      }

      return await updateUser(selectedUser.id, {
        roleIds: selectedRoleIds,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast({
        title: "Roles updated",
        description: `Roles for ${selectedUser?.name} have been updated successfully.`,
      });
      setIsRolesDialogOpen(false);
    },
    onError: (error) => {
      console.error("Error updating user roles:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to update user roles. Please try again.",
        variant: "destructive",
      });
    },
  });

  const getSubscriptionStatusColor = (isActive: boolean) => {
    return isActive
      ? "bg-green-500/10 text-green-600 border-green-200"
      : "bg-gray-500/10 text-gray-600 border-gray-200";
  };

  const handleRowClick = (userId: number) => {
    navigate(`/admin/users/${userId}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return (
      <div className="flex flex-col">
        <span>{format(date, "MMM d, yyyy")}</span>
        <span className="text-xs text-muted-foreground">
          {format(date, "h:mm a")}
        </span>
      </div>
    );
  };

  // Open manage roles dialog for a user
  const handleManageRoles = (e: React.MouseEvent, user: User) => {
    e.stopPropagation();

    // Check if the current user has admin privileges
    if (!hasAdminPrivileges()) {
      toast({
        title: "Permission denied",
        description: "Only administrators can manage user roles.",
        variant: "destructive",
      });
      return;
    }

    setSelectedUser(user);
    setSelectedRoleIds(user.roleIds || []);
    setUnauthorizedMessage("");
    setIsRolesDialogOpen(true);
  };

  // Handle role selection
  const handleRoleToggle = (roleId: number) => {
    // Check if user can manage this role
    if (!canManageRole(roleId)) {
      setUnauthorizedMessage("You don't have permission to assign this role.");
      return;
    }

    setUnauthorizedMessage("");
    setSelectedRoleIds((prev) => {
      if (prev.includes(roleId)) {
        return prev.filter((id) => id !== roleId);
      } else {
        return [...prev, roleId];
      }
    });
  };

  // Determine if there have been changes to the roles
  const hasRoleChanges = () => {
    if (!selectedUser) return false;

    if (selectedRoleIds.length !== selectedUser.roleIds.length) {
      return true;
    }

    // Check if every initial role ID is in the selected roles
    return (
      !selectedUser.roleIds.every((id) => selectedRoleIds.includes(id)) ||
      !selectedRoleIds.every((id) => selectedUser.roleIds.includes(id))
    );
  };

  // Save role changes
  const handleSaveRoles = () => {
    if (selectedRoleIds.length === 0) {
      toast({
        title: "Error",
        description: "User must have at least one role assigned.",
        variant: "destructive",
      });
      return;
    }

    updateRolesMutation.mutate();
  };

  // Get the role name from its ID
  const getRoleName = (roleId: number): string => {
    const role = userRoles.find((r) => r.id === roleId);
    return role?.name || "Unknown";
  };

  // Get plan badge color
  const getPlanBadgeColor = (planName: string): string => {
    const planColors: Record<string, string> = {
      Free: "bg-gray-500/10 text-gray-600 border-gray-200",
      Basic: "bg-blue-500/10 text-blue-500 border-blue-200",
      Standard: "bg-green-500/10 text-green-500 border-green-200",
      Premium: "bg-purple-500/10 text-purple-500 border-purple-200",
      Professional: "bg-amber-500/10 text-amber-500 border-amber-200",
      Enterprise: "bg-indigo-500/10 text-indigo-500 border-indigo-200",
    };

    return (
      planColors[planName] || "bg-primary/10 text-primary border-primary/20"
    );
  };

  // Get subscription plan for a user
  const getUserSubscription = (
    userId: number
  ): UserSubscription | undefined => {
    // Prefer latest (any status); fall back to active map if missing
    const latest = userSubscriptions?.get(userId);
    if (latest) return latest;
    return activeUserSubscriptions?.get(userId);
  };

  // Get expiry status color
  const getExpiryStatusColor = (expiryDate: string | null): string => {
    if (!expiryDate) return "bg-gray-500/10 text-gray-600 border-gray-200";

    const now = new Date();
    const expiry = new Date(expiryDate);
    const oneWeekFromNow = new Date();
    oneWeekFromNow.setDate(oneWeekFromNow.getDate() + 7);

    if (isPast(expiry)) {
      return "bg-red-500/10 text-red-500 border-red-200"; // Expired
    } else if (isBefore(expiry, oneWeekFromNow)) {
      return "bg-amber-500/10 text-amber-500 border-amber-200"; // Expiring soon
    } else {
      return "bg-green-500/10 text-green-500 border-green-200"; // Active
    }
  };

  // Format expiry date or return status
  const formatExpiryDate = (
    expiryDate: string | null
  ): { text: string; status: string; timeLeft?: string } => {
    if (!expiryDate) return { text: "No expiry date", status: "none" };

    const expiry = new Date(expiryDate);
    const now = new Date();
    const oneWeekFromNow = new Date();
    oneWeekFromNow.setDate(oneWeekFromNow.getDate() + 7);

    if (isPast(expiry)) {
      return {
        text: format(expiry, "MMM d, yyyy"),
        status: "expired",
      };
    } else if (isBefore(expiry, oneWeekFromNow)) {
      // Calculate time remaining
      const diffMs = expiry.getTime() - now.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHrs = Math.floor(
        (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );

      let timeLeft = "";
      if (diffDays > 0) {
        timeLeft = `${diffDays}d ${diffHrs}h`;
      } else {
        const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        timeLeft = `${diffHrs}h ${diffMins}m`;
      }

      return {
        text: format(expiry, "MMM d, yyyy"),
        status: "expiring-soon",
        timeLeft,
      };
    } else {
      return {
        text: format(expiry, "MMM d, yyyy"),
        status: "active",
      };
    }
  };

  // Show hover tooltip with full expiry info
  const getExpiryTooltip = (status: string, date: string) => {
    const expiry = new Date(date);
    const now = new Date();

    if (status === "expired") {
      const daysSince = Math.floor(
        (now.getTime() - expiry.getTime()) / (1000 * 60 * 60 * 24)
      );
      return `Expired ${daysSince} days ago on ${format(
        expiry,
        "MMMM d, yyyy"
      )}`;
    } else if (status === "expiring-soon") {
      const diffMs = expiry.getTime() - now.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHrs = Math.floor(
        (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const diffSecs = Math.floor((diffMs % (1000 * 60)) / 1000);

      let timeDetail = "";
      if (diffDays > 0) {
        timeDetail = `${diffDays} days, ${diffHrs} hours, ${diffMins} minutes`;
      } else if (diffHrs > 0) {
        timeDetail = `${diffHrs} hours, ${diffMins} minutes, ${diffSecs} seconds`;
      } else {
        timeDetail = `${diffMins} minutes, ${diffSecs} seconds`;
      }

      return `Expires in ${timeDetail} (${format(expiry, "MMMM d, yyyy")})`;
    } else {
      const diffDays = Math.floor(
        (expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      );
      return `Active for ${diffDays} more days until ${format(
        expiry,
        "MMMM d, yyyy"
      )}`;
    }
  };

  return (
    <>
      {users.map((user) => (
        <TableRow
          key={user.id}
          className="hover:bg-muted/50 cursor-pointer transition-colors"
          onClick={() => handleRowClick(user.id)}
        >
          <TableCell className="font-medium">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10 border border-border">
                <AvatarImage src={user.avatarUrl} alt={user.name} />
                <AvatarFallback className="bg-primary/10 text-primary">
                  {user.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span className="font-medium">{user.name}</span>
                <span className="text-xs text-muted-foreground">
                  {user.email}
                </span>
              </div>
            </div>
          </TableCell>
          <TableCell>
            <Badge
              variant="outline"
              className={cn(
                "border",
                user.name === "New User"
                  ? "bg-amber-500/10 text-amber-500 border-amber-200"
                  : getSubscriptionStatusColor(user.isActive)
              )}
            >
              {user.name === "New User"
                ? "Pending"
                : user.isActive
                ? "Active"
                : "Inactive"}
            </Badge>
          </TableCell>
          <TableCell>
            {(() => {
              const subscription = getUserSubscription(user.id);
              if (subscription?.subscriptionPlan) {
                const expiry = subscription.subscriptionEnd
                  ? new Date(subscription.subscriptionEnd)
                  : null;
                const isExpired = expiry ? isPast(expiry) : false;

                if (isExpired) {
                  return (
                    <Badge
                      variant="outline"
                      className="border bg-red-500/10 text-red-500 border-red-200"
                      title={`Last plan: ${
                        subscription.subscriptionPlan.name
                      } • Expired on ${format(expiry as Date, "MMM d, yyyy")}`}
                    >
                      Expired
                    </Badge>
                  );
                }

                return (
                  <Badge
                    variant="outline"
                    className={cn(
                      "border",
                      getPlanBadgeColor(subscription.subscriptionPlan.name)
                    )}
                  >
                    {subscription.subscriptionPlan.name}
                  </Badge>
                );
              } else if (
                user.roles?.some((role) => role.name === "Super Admin")
              ) {
                return (
                  <Badge
                    variant="outline"
                    className="bg-indigo-500/10 text-indigo-500 border-indigo-200"
                  >
                    Admin Access
                  </Badge>
                );
              } else {
                return (
                  <Badge
                    variant="outline"
                    className="border-muted-foreground/20 text-muted-foreground"
                  >
                    No Plan
                  </Badge>
                );
              }
            })()}
          </TableCell>
          <TableCell>
            {(() => {
              const subscription = getUserSubscription(user.id);
              if (subscription?.subscriptionEnd) {
                const { text, status, timeLeft } = formatExpiryDate(
                  subscription.subscriptionEnd
                );
                const tooltipText = getExpiryTooltip(
                  status,
                  subscription.subscriptionEnd
                );
                return (
                  <div className="flex items-center group relative">
                    <div
                      className={cn(
                        "w-2 h-2 rounded-full mr-2 mt-0.5",
                        status === "expired"
                          ? "bg-red-500"
                          : status === "expiring-soon"
                          ? "bg-amber-500"
                          : "bg-green-500"
                      )}
                    ></div>
                    <div className="flex flex-col">
                      <span className="text-xs">
                        {format(
                          new Date(subscription.subscriptionEnd),
                          "MMM d, yyyy"
                        )}
                      </span>
                      {status === "expiring-soon" && timeLeft && (
                        <span className="text-xs text-muted-foreground">
                          {timeLeft}
                        </span>
                      )}
                    </div>
                    <div className="absolute bottom-full left-0 bg-black text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity z-50 mb-2 whitespace-nowrap">
                      {tooltipText}
                    </div>
                  </div>
                );
              } else if (
                user.roles?.some((role) => role.name === "Super Admin")
              ) {
                return <span className="text-xs text-muted-foreground">—</span>;
              } else {
                return (
                  <span className="text-xs text-muted-foreground">None</span>
                );
              }
            })()}
          </TableCell>
          <TableCell>
            {user.roles && user.roles.length > 0 ? (
              <div className="flex gap-1 flex-wrap">
                {user.roles.slice(0, 2).map((role) => (
                  <Badge
                    key={role.id}
                    variant="outline"
                    className={cn("text-xs border", getRoleBadgeColor(role.id))}
                  >
                    {role.name}
                  </Badge>
                ))}
                {user.roles.length > 2 && (
                  <Badge variant="outline" className="text-xs border-border">
                    +{user.roles.length - 2}
                  </Badge>
                )}
              </div>
            ) : (
              <span className="text-muted-foreground text-xs">No role</span>
            )}
          </TableCell>
          <TableCell>
            {formatDate(user.registeredAt)}
            <span className="text-xs text-muted-foreground block mt-1">
              {formatDistanceToNow(new Date(user.registeredAt), {
                addSuffix: true,
              })}
            </span>
          </TableCell>
          <TableCell className="text-right">
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/admin/users/${user.id}`);
                  }}
                >
                  <Edit className="mr-2 h-4 w-4" />
                  View Details
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => handleManageRoles(e, user)}>
                  <UserCog className="mr-2 h-4 w-4" />
                  Manage Roles
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/admin/users/${user.id}?tab=subscription`);
                  }}
                >
                  <CreditCard className="mr-2 h-4 w-4" />
                  Manage Subscription
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </TableCell>
        </TableRow>
      ))}

      {/* Manage Roles Dialog */}
      <Dialog open={isRolesDialogOpen} onOpenChange={setIsRolesDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Manage Roles for {selectedUser?.name}
            </DialogTitle>
            <DialogDescription>
              Assign or remove roles for this user. Each role provides different
              permissions and access levels.
            </DialogDescription>
          </DialogHeader>

          {selectedUser && (
            <div className="py-4">
              <div className="space-y-4">
                <div className="text-sm font-medium">Current Roles:</div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {selectedUser.roles && selectedUser.roles.length > 0 ? (
                    selectedUser.roles.map((role) => (
                      <Badge
                        key={role.id}
                        variant="outline"
                        className={cn("border", getRoleBadgeColor(role.id))}
                      >
                        {role.isSystem && <Shield className="h-3 w-3 mr-1" />}
                        {role.name}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-muted-foreground text-sm">
                      No roles assigned
                    </span>
                  )}
                </div>

                {/* Display warning message if needed */}
                {unauthorizedMessage && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3 mb-4">
                    <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-400">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm">{unauthorizedMessage}</span>
                    </div>
                  </div>
                )}

                <div className="text-sm font-medium border-t pt-4">
                  Available Roles:
                </div>
                <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                  {userRoles.map((role) => {
                    const isDisabled = !canManageRole(role.id);

                    return (
                      <div
                        key={role.id}
                        className={cn(
                          "flex items-center space-x-3 rounded-md border p-3",
                          selectedRoleIds.includes(role.id) && "bg-muted/50",
                          isDisabled && "opacity-60"
                        )}
                      >
                        <Checkbox
                          id={`role-${role.id}`}
                          checked={selectedRoleIds.includes(role.id)}
                          onCheckedChange={() => handleRoleToggle(role.id)}
                          disabled={isDisabled}
                        />
                        <label htmlFor={`role-${role.id}`}>
                          {role.isSystem && (
                            <ShieldAlert className="h-3.5 w-3.5 inline-block mr-1 text-primary" />
                          )}
                          {role.name}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setIsRolesDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveRoles}
              disabled={updateRolesMutation.isPending}
            >
              {updateRolesMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UserTable;
